
import Vue from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';
import values from 'lodash/values';

import FormInput from '@/components/controls/form-input.vue';
import SelectInput from '@/components/controls/select-input.vue';
import NotificationBanner from '@/components/blocks/notification-banner.vue';
import Publish from '@/components/forms/publish.vue';
import CountrySelection from '@/components/controls/country-selection.vue';
import SendNewDeviceEmailModal from '@/components/forms/send-new-device-email-modal.vue';
import Modal from '@/components/layout/modal.vue';
import SubmitButton from '@/components/controls/submit-button.vue';

import { ListStatus, RootActions } from '@/store/types';
import Practice from '@/store/modules/practices/practice';
import { PracticesActions, PracticeStatus } from '@/store/modules/practices/types';
import Autocomplete, {
  AutocompleteOption,
  AutocompleteResponse,
  AutocompletePromise,
} from '@/components/controls/autocomplete.vue';
import Product from '@/store/modules/products/product';
import ProductRelationship from '@/store/modules/products/product-relationship';
import Taxonomy from '@/store/models/taxonomy';
import TreatmentEnhancement from '@/store/modules/treatments/enhancement';

export default Vue.extend({
  inject: {
    $validator: '$validator',
  },
  name: 'EditPracticeView',
  components: {
    FormInput,
    SelectInput,
    NotificationBanner,
    Publish,
    Autocomplete,
    CountrySelection,
    SendNewDeviceEmailModal,
    Modal,
    SubmitButton,
  },
  props: {
    add: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      productsInput: '',
      productAccessoriesInput: '',
      enhancementsInput: '',
      resourceProductsInput: '',
      isSendNewDeviceEmailModalOpen: false,
      isNewDeviceEmailPromptOpen: false,
      newDeviceProductId: 0,
    };
  },
  computed: {
    ...mapState('practices', {
      practice: 'editablePractice',
      editableStatus: 'editableStatus',
    }),
    ...mapGetters('practices', {
      isLoading: 'editableIsLoading',
      isSaving: 'editableIsSaving',
      hasUnsavedChanges: 'editableHasUnsavedChanges',
    }),
    ...mapGetters('user/profile', ['isDistributor', 'distributorRegion']),
    ...mapState({
      regions: 'regions',
      regionsStatus: 'regionsStatus',
      countries: 'countries',
      countriesStatus: 'countriesStatus',
    }),
    ...mapState('products', {
      productsTree: 'tree',
    }),
    ...mapState('treatments', {
      enhancementsTree: 'enhancementsTree',
    }),
    isFetching() {
      return this.isLoading || this.isSaving;
    },
    areRegionsLoaded() {
      return this.regionsStatus === ListStatus.Loaded;
    },
    practiceRegionOptions() {
      const list = this.regions as { id: number | string; name: string }[];
      if (this.isDistributor) {
        const allow = new Set(this.distributorRegion.map((id: number) => Number(id)));
        return list.filter((r) => allow.has(Number(r.id)));
      }
      return list;
    },
    countryFilterByRegions(): number[] | null {
      if (this.isDistributor) {
        const ids = this.distributorRegion;
        return ids.length ? ids : [];
      }
      return null;
    },
    isInactive() {
      return this.practice.status === PracticeStatus.Inactive;
    },
    getProducts(): AutocompleteOption[] {
      const productsIds = this.practice.products.map((product) => product.id);

      return this.productsTree
        .filter((product) => productsIds.indexOf(product.id) === -1)
        .map((product) => ({
          label: product.title,
          value: product.id,
        }))
        .filter((option) => option.label.toLowerCase().indexOf(this.productsInput.toLowerCase()) > -1);
    },
    getResourceProducts(): AutocompleteOption[] {
      const productsIds = this.practice.products.map((product) => product.id);
      const resourceProductsIds = this.practice.resourceProducts.map((product) => product.id);

      return this.productsTree
        .filter((product) => resourceProductsIds.indexOf(product.id) === -1)
        .filter((product) => productsIds.indexOf(product.id) === -1)
        .map((product) => ({
          label: product.title,
          value: product.id,
        }))
        .filter((option) => option.label.toLowerCase().indexOf(this.resourceProductsInput.toLowerCase()) > -1);
    },

    practiceResourceProducts() {
      const productsIds = this.practice.products.map((product) => product.id);
      return this.practice.resourceProducts.filter((product) => productsIds.indexOf(product.id) === -1);
    },
    allProductAccessories() {
      const productsIds = this.practice.products.map((product) => product.id);
      const initial: { [key: number]: Taxonomy } = {};

      return values(
        this.productsTree
          .filter((product) => productsIds.indexOf(product.id) !== -1)
          .map((product) => product.accessories)
          .reduce((res, accessories) => {
            accessories.forEach((accessory) => {
              res[accessory.id] = accessory;
            });

            return res;
          }, initial),
      );
    },

    getProductAccessories(): Taxonomy[] {
      const ids = this.selectedProductAccessories.map(({ id }) => id);
      return this.allProductAccessories.filter(({ id }) => ids.indexOf(id) === -1);
    },

    getProductAccessoriesOptions(): AutocompleteOption[] {
      return this.getProductAccessories
        .map((accessory) => ({
          label: accessory.title,
          value: accessory.id,
        }))
        .filter(({ label }) => label.toLowerCase().indexOf(this.productAccessoriesInput.toLowerCase()) > -1);
    },

    hasProductAccessories(): boolean {
      return Boolean(this.allProductAccessories.length);
    },

    hasProductAccessoriesOptions(): boolean {
      return Boolean(this.getProductAccessories.length);
    },

    selectedProductAccessories(): Taxonomy[] {
      const availableIds = this.allProductAccessories.map(({ id }) => id);

      return this.practice.accessories.filter(({ id }) => availableIds.indexOf(id) !== -1);
    },
    getTreatmentEnhancements(): AutocompleteOption[] {
      const ids = this.practice.enhancements.map(({ id }) => id);

      return this.enhancementsTree
        .filter(({ id }) => ids.indexOf(id) === -1)
        .map(({ id, title }) => ({
          label: title,
          value: id,
        }))
        .filter(({ label }) => label.toLowerCase().indexOf(this.enhancementsInput.toLowerCase()) > -1);
    },
    practiceLanguageCode(): string {
      // Get language code from first user if available, otherwise default to 'en'
      if (this.practice.users && this.practice.users.length > 0 && this.practice.users[0].languageCode) {
        return this.practice.users[0].languageCode;
      }
      return 'en';
    },
  },
  methods: {
    ...mapActions('practices', {
      updatePractice: PracticesActions.UPDATE_EDITABLE,
      save: PracticesActions.SAVE_EDITABLE,
      removePractice: PracticesActions.DELETE,
      restorePractice: PracticesActions.RESTORE,
    }),
    'addProduct'(option: AutocompleteOption) {
      const products = [...this.practice.products];
      const product = this.productsTree.find((item) => item.id === option.value);
      if (!product) {
        return;
      }

      products.push(ProductRelationship.fromJSON(product.toJSON()));

      this.updatePractice({ products });
    },

    'toggleTrackingCheckbox'(event: InputEvent) {
      const target = event.target! as HTMLInputElement;
      const tracking = target.checked ? 1 : 0;

      this.updatePractice({ liineTracking: tracking });
    },

    'removeProduct'(productId: number) {
      const products = this.practice.products.filter((product) => product.id !== productId);

      this.updatePractice({ products });
    },

    'addResourceProduct'(option: AutocompleteOption) {
      const resourceProducts = [...this.practice.resourceProducts];
      const product = this.productsTree.find((item) => item.id === option.value);
      if (!product) {
        return;
      }

      resourceProducts.push(ProductRelationship.fromJSON(product.toJSON()));

      this.updatePractice({ resourceProducts });
    },

    'removeResourceProduct'(productId: number) {
      const resourceProducts = this.practice.resourceProducts.filter((product) => product.id !== productId);

      this.updatePractice({ resourceProducts });
    },

    'addProductAccessory'(option: AutocompleteOption) {
      const accessories = [...this.practice.accessories];
      const accessory = this.allProductAccessories.find((item) => item.id === option.value);
      if (!accessory) {
        return;
      }

      accessories.push(accessory);

      this.updatePractice({ accessories });
    },

    'removeProductAccessory'(accessoryId: number) {
      const accessories = this.selectedProductAccessories.filter(({ id }) => id !== accessoryId);

      this.updatePractice({ accessories });
    },

    'addTreatmentEnhancement'(option: AutocompleteOption) {
      const enhancements = [...this.practice.enhancements];
      const enhancement = this.enhancementsTree.find((item) => item.id === option.value);
      if (!enhancement) {
        return;
      }

      enhancements.push(Taxonomy.fromJSON(enhancement.toJSON()));

      this.updatePractice({ enhancements });
    },

    'removeTreatmentEnhancement'(enhancementId: number) {
      const enhancements = this.practice.enhancements.filter(({ id }) => id !== enhancementId);

      this.updatePractice({ enhancements });
    },

    'remove'() {
      this.removePractice({ id: this.practice.id });
    },

    'restore'() {
      this.restorePractice({ id: this.practice.id });
    },

    'closeNewDeviceEmailPrompt'() {
      const { open_new_device_email_modal, product_id, ...query } = this.$route.query;

      this.isNewDeviceEmailPromptOpen = false;
      this.$router.replace({ query });
    },
    'openNewDeviceEmailModal'() {
      const productId = this.$route.query.product_id;
      if (productId) {
        this.newDeviceProductId = Number(productId);
      }
      this.isSendNewDeviceEmailModalOpen = true;
      this.closeNewDeviceEmailPrompt();
    },
    'onRegionInput'(regionId: number) {
      const payload: { regionId: number; countryId: number } = { regionId, countryId: 0 };
      if (regionId && this.practice.countryId) {
        const c = (this.countries as { id: number | string; regionId?: number }[]).find(
          (co) => Number(co.id) === Number(this.practice.countryId),
        );
        if (c && Number(c.regionId) === Number(regionId)) {
          payload.countryId = Number(this.practice.countryId);
        }
      }
      this.updatePractice(payload);
    },

    'syncRegionFromCountry'() {
      if (this.countriesStatus !== ListStatus.Loaded || !this.practice) {
        return;
      }
      if (this.practice.regionId || !this.practice.countryId) {
        return;
      }
      const c = (this.countries as { id: number | string; regionId?: number }[]).find(
        (co) => Number(co.id) === Number(this.practice.countryId),
      );
      if (c && c.regionId) {
        this.updatePractice({ regionId: Number(c.regionId) });
      }
    },
    created() {
      this.$store.dispatch(RootActions.LOAD_REGIONS, { force: false });
    },
    watch: {
      'countriesStatus'(val) {
        if (val === ListStatus.Loaded) {
          this.$nextTick(() => this.syncRegionFromCountry());
        }
      },
      'practice.countryId'() {
        this.$nextTick(() => this.syncRegionFromCountry());
      },
      '$route.query.open_new_device_email_modal': {
        handler(value, prevValue) {
          if (value !== prevValue) {
            if (value === '1') {
              const productId = this.$route.query.product_id;
              if (productId) {
                this.newDeviceProductId = Number(productId);
              }
              //this.isNewDeviceEmailPromptOpen = true;
            } else {
              //this.isNewDeviceEmailPromptOpen = false;
            }
          }
        },
        immediate: true,
      },
    },
    beforeRouteLeave(to, from, next) {
      if (this.hasUnsavedChanges) {
        const answer = window.confirm(this.$t('Do you really want to leave? You have unsaved changes!'));

        if (!answer) {
          next(false);
          return;
        }
      }

      next();
    },
  },
});
